import { action, observable, computed, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import type { JSONContent } from '@tiptap/core';
import type { NumberingFormatDefinition } from '@tiptap-pro/extension-export-docx';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import {
  EntityValueType,
  DocumentAccessLevel,
  DefaultDocumentAccess,
  DocumentPermissionKind,
  DocumentPermissionRequest,
  DocumentPermissionResponse,
} from 'utils/api/modules/entities/entities.types';
import { isConflict, isNotFound } from 'utils/api/errors';

import { PickerItemKind, PickerValue } from 'components/SharedFormComponents/UserAndGroupPicker';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import { DocumentViewMode } from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import type { ImportedHeaderFooter } from 'containers/Tiptap/lib/import-utils';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

export type DraftPermission =
  | {
      kind: DocumentPermissionKind.User;
      uuid: string;
      userUuid: string;
      userName: string;
      accessLevel: DocumentAccessLevel;
    }
  | {
      kind: DocumentPermissionKind.Group;
      uuid: string;
      groupUuid: string;
      groupName: string;
      memberCount: number;
      accessLevel: DocumentAccessLevel;
    };

const groupUuidsIn = (permissions: DocumentPermissionResponse[]): string[] =>
  permissions
    .filter(
      (p): p is Extract<DocumentPermissionResponse, { kind: DocumentPermissionKind.Group }> =>
        p.kind === DocumentPermissionKind.Group
    )
    .map((p) => p.group.uuid);

export default class DocumentsDetailsStore extends EntityDetails {
  @observable accessLevel: DocumentAccessLevel | null = null;
  @observable isOwner = false;
  @observable documentToken: string | null = null;
  @observable permissions: DocumentPermissionResponse[] = [];
  @observable deleteBlockedByActiveRequests: string | null = null;

  // Stashed .docx import JSON; the editor seeds it on first sync then clears it.
  // Keyed by entityId so a stale stash can't leak into the wrong document.
  @observable pendingInitialContent: {
    entityId: string;
    json: JSONContent;
    numberingFormats: NumberingFormatDefinition[];
    viewMode: DocumentViewMode;
    headerFooter: ImportedHeaderFooter | null;
  } | null = null;

  // Draft state for the share modal
  @observable draftAdds: DraftPermission[] = [];
  @observable draftUpdates: Record<string, DocumentAccessLevel> = {};
  @observable draftRemoves: Set<string> = new Set();
  @observable draftDefaultAccess: DefaultDocumentAccess = DefaultDocumentAccess.AllEdit;
  @observable saving = false;

  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Documents, routes.dashboard.documents.toString(), (uuid: string): string =>
      reverse(routes.dashboard.documents.details, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @computed
  get originalDefaultAccess(): DefaultDocumentAccess {
    return this.details?.defaultAccess ?? DefaultDocumentAccess.AllEdit;
  }

  @computed
  get displayPermissions(): DocumentPermissionResponse[] {
    const existing = this.permissions
      .filter((p) => !this.draftRemoves.has(p.uuid))
      .map((p) => (this.draftUpdates[p.uuid] ? { ...p, accessLevel: this.draftUpdates[p.uuid] } : p));

    const pending = this.draftAdds.map((p): DocumentPermissionResponse => {
      if (p.kind === DocumentPermissionKind.Group) {
        return {
          uuid: p.uuid,
          kind: DocumentPermissionKind.Group,
          group: { uuid: p.groupUuid, name: p.groupName, memberCount: p.memberCount },
          accessLevel: p.accessLevel,
          createdAt: '',
          createdBy: { uuid: '', name: '' },
        };
      }
      return {
        uuid: p.uuid,
        kind: DocumentPermissionKind.User,
        user: { uuid: p.userUuid, name: p.userName },
        accessLevel: p.accessLevel,
        createdAt: '',
        createdBy: { uuid: '', name: '' },
      };
    });

    return [...existing, ...pending];
  }

  @computed
  get pendingGroupUuids(): string[] {
    return groupUuidsIn(this.displayPermissions);
  }

  @computed
  get savedGroupUuids(): string[] {
    return groupUuidsIn(this.permissions);
  }

  @action.bound
  resetDraft(): void {
    this.draftAdds = [];
    this.draftUpdates = {};
    this.draftRemoves = new Set();
    this.draftDefaultAccess = this.originalDefaultAccess;
  }

  @action.bound
  addDraftPermission(item: PickerValue, accessLevel: DocumentAccessLevel): void {
    const uuid = `pending-${item.kind}-${item.uuid}-${Date.now()}`;
    if (item.kind === PickerItemKind.Group) {
      this.draftAdds = [
        ...this.draftAdds,
        {
          kind: DocumentPermissionKind.Group,
          uuid,
          groupUuid: item.uuid,
          groupName: item.name,
          memberCount: item.memberCount,
          accessLevel,
        },
      ];
      return;
    }
    this.draftAdds = [
      ...this.draftAdds,
      { kind: DocumentPermissionKind.User, uuid, userUuid: item.uuid, userName: item.name, accessLevel },
    ];
  }

  @action.bound
  updateDraftPermission(permissionId: string, accessLevel: DocumentAccessLevel): void {
    const pendingIdx = this.draftAdds.findIndex((p) => p.uuid === permissionId);
    if (pendingIdx >= 0) {
      this.draftAdds = this.draftAdds.map((p) => (p.uuid === permissionId ? { ...p, accessLevel } : p));
      return;
    }

    this.draftUpdates = { ...this.draftUpdates, [permissionId]: accessLevel };
  }

  @action.bound
  removeDraftPermission(permissionId: string): void {
    const pendingIdx = this.draftAdds.findIndex((p) => p.uuid === permissionId);
    if (pendingIdx >= 0) {
      this.draftAdds = this.draftAdds.filter((p) => p.uuid !== permissionId);
      return;
    }

    this.draftRemoves = new Set(this.draftRemoves).add(permissionId);
    const { [permissionId]: _, ...rest } = this.draftUpdates;
    this.draftUpdates = rest;
  }

  @action.bound
  setDraftDefaultAccess(defaultAccess: DefaultDocumentAccess): void {
    this.draftDefaultAccess = defaultAccess;
  }

  @action.bound
  async saveDraft(): Promise<boolean> {
    if (!this.details?.uuid) return false;
    this.saving = true;

    try {
      const groupsEnabled = !!this.store.session.user?.groupsEnabled;
      const desiredPermissions: DocumentPermissionRequest[] = this.displayPermissions
        .filter((p) => groupsEnabled || p.kind !== DocumentPermissionKind.Group)
        .map((p) =>
          p.kind === DocumentPermissionKind.Group
            ? { kind: DocumentPermissionKind.Group, groupUuid: p.group.uuid, accessLevel: p.accessLevel }
            : { kind: DocumentPermissionKind.User, userUuid: p.user.uuid, accessLevel: p.accessLevel }
        );

      await this.syncPermissions(desiredPermissions);

      if (this.draftDefaultAccess !== this.originalDefaultAccess) {
        await this.updateDefaultAccess(this.draftDefaultAccess);
      }

      this.store.notification.enqueueSuccessSnackbar('Permissions updated');
      return true;
    } catch (e) {
      Logger.error('Failed to save permissions', e);
      this.store.notification.enqueueErrorSnackbar('Failed to update permissions');
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  @action.bound
  async loadDetails(documentId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, documentId)();
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, documentId, this.entityType);
        this.error = undefined;
        this.details = data;
      });

      await this.loadDocumentToken(documentId);
    } catch (e) {
      const error = e as AxiosError<string[]>;
      runInAction(() => {
        this.error = error;
      });
      if (!isNotFound(error)) {
        Logger.error(`Invalid load document details API response. Task id: ${documentId}`, error);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  async loadDocumentToken(documentId: string): Promise<void> {
    try {
      const { data } = await this.api.entities.loadDocumentToken(this.entityType, documentId)();
      runInAction(() => {
        this.documentToken = data.token;
        this.accessLevel = data.accessLevel;
        this.isOwner = data.isOwner;
      });
    } catch (e) {
      Logger.error('Failed to load document token', e);
    }
  }

  async fetchImageUploadToken(entityId: string): Promise<string | null> {
    try {
      const { data } = await this.api.entities.loadDocumentToken(this.entityType, entityId)();
      return data.imageUploadToken;
    } catch (e) {
      Logger.error('Failed to load image upload token', e);
      return null;
    }
  }

  @action.bound
  async loadPermissions(): Promise<void> {
    if (!this.details?.uuid) return;
    try {
      const { data } = await this.api.entities.loadDocumentPermissions(this.entityType, this.details.uuid)();
      runInAction(() => {
        this.permissions = data;
      });
    } catch (e) {
      Logger.error('Failed to load document permissions', e);
    }
  }

  @action.bound
  async syncPermissions(entries: DocumentPermissionRequest[]): Promise<boolean> {
    if (!this.details?.uuid) return false;
    try {
      const { data } = await this.api.entities.syncDocumentPermissions(this.entityType, this.details.uuid, entries)();
      runInAction(() => {
        this.permissions = data;
      });
      return true;
    } catch (e) {
      Logger.error('Failed to sync document permissions', e);
      this.store.notification.enqueueErrorSnackbar('Failed to update permissions');
      return false;
    }
  }

  @action.bound
  async updateDefaultAccess(defaultAccess: DefaultDocumentAccess): Promise<boolean> {
    if (!this.details?.uuid) return false;
    try {
      await this.api.entities.updateDocumentAccess(this.entityType, this.details.uuid, { defaultAccess })();
      runInAction(() => {
        if (this.details) {
          this.details = { ...this.details, defaultAccess };
        }
      });
      return true;
    } catch (e) {
      Logger.error('Failed to update default access', e);
      return false;
    }
  }

  @action.bound
  async transferOwnership(newOwnerUuid: string): Promise<boolean> {
    if (!this.details?.uuid) return false;
    try {
      const { data } = await this.api.entities.transferDocumentOwner(
        this.entityType,
        this.details.uuid,
        newOwnerUuid
      )();
      runInAction(() => {
        if (this.details) {
          this.details = { ...this.details, owner: data.owner };
        }
        this.isOwner = false;
      });
      this.store.notification.enqueueSuccessSnackbar('Ownership transferred');
      return true;
    } catch (e) {
      Logger.error('Failed to transfer ownership', e);
      this.store.notification.enqueueErrorSnackbar('Failed to transfer ownership');
      return false;
    }
  }

  create = async (documentName: string, folderUuid?: string | null): Promise<boolean | string> => {
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(
        this.entityType,
        { [`${this.prefix}name`]: documentName },
        folderUuid || null
      )();
      await this.store.folders.loadFolders();
      return uuid;
    } catch (e) {
      Logger.error('Failed to create document', e);

      return false;
    }
  };

  @action.bound
  setPendingInitialContent(
    entityId: string,
    json: JSONContent,
    numberingFormats: NumberingFormatDefinition[],
    viewMode: DocumentViewMode,
    headerFooter: ImportedHeaderFooter | null = null
  ): void {
    this.pendingInitialContent = { entityId, json, numberingFormats, viewMode, headerFooter };
  }

  @action.bound
  clearPendingInitialContent(): void {
    this.pendingInitialContent = null;
  }

  @action.bound
  clearDeleteBlocked(): void {
    this.deleteBlockedByActiveRequests = null;
  }

  delete = async (documentId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, documentId)();
    } catch (e) {
      const error = e as AxiosError<unknown>;
      if (isConflict(error)) {
        runInAction(() => {
          this.deleteBlockedByActiveRequests = documentId;
        });
        return false;
      }
      Logger.error('Failed to delete document', e);
      return false;
    }
    return true;
  };

  moveToFolder = async (documentId: string, folderId: string): Promise<boolean> => {
    try {
      await this.api.entities.editEntity(this.entityType, documentId, {}, folderId)();
      return true;
    } catch (e) {
      Logger.error('Failed to move document to folder', e);
      return false;
    }
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity(this.entityType, this.details?.uuid, data)();
      this.details = edited.data;
      this.isContentEdited = false;
      this.store.notification.enqueueSuccessSnackbar('Document edited successfully');
      await this.store.folders.loadFolders();
      return true;
    } catch (e) {
      const error = e as AxiosError<string[]>;
      this.store.notification.enqueueErrorSnackbar(
        error.response?.data?.[0] || `The document could not be updated. Something went wrong.`
      );
      Logger.error('Failed to edit document', e);
      return false;
    }
  };

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    if (question?.value !== newValue) {
      this.isContentEdited = true;
    } else {
      this.isContentEdited = false;
    }
  }

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
    this.accessLevel = null;
    this.isOwner = false;
    this.documentToken = null;
    this.permissions = [];
    this.draftAdds = [];
    this.draftUpdates = {};
    this.draftRemoves = new Set();
    this.draftDefaultAccess = DefaultDocumentAccess.AllEdit;
    this.saving = false;
  }
}
